import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { SFInformationIcon } from "../../icons";
import { SFStack, SFGridContainer, SFGridItem } from "../../grid";
import SFAccordionCard from "../../accordion/accordionCard";
import SFQueryBuilder from "../../queryBuilder";
import getStyled from "../../../hooks/getStyled";
import { getGridFilterValues } from "../../../services/dataGridService";
import SFTypography from "../../typography";
import SFTextField from "../../textField";
import SFDivider from "../../divider";
import Actions from "./actions";
import ConfirmationDialog from "./confirmationDialog";
import { ACTION_TYPES } from "./actionTypes";
import {
  setAdvancedQueryPanel,
  setSelectedQuery,
} from "../../../redux/reducers/gridLayoutReducer";
import { transformQuery } from "react-querybuilder";
import { toastActions } from "@base-provider";
import { saveQuery, updateQuery } from "../../../services/savedQueryService";
import { validateDateRangePickerInput } from "./helpers/validateDateRangePickerInput";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const Wrapper = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiAccordionDetails-root": {
    paddingBottom: theme.spacing(1.5),
  },
}));

const SFDividerStyled = styled(SFDivider)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const SFDividerActionsStyled = styled(SFDivider)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
}));

const AdvancedQuery = ({
  gridName,
  columnDefs,
  filterValuesUrl,
  onApplyFilters,
  advancedFilterProps,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const savedLayout = useSelector((state) => {
    return state.gridLayout.find(
      (gridLayout) => gridLayout.gridName === gridName
    );
  });

  let externalFilterModel = savedLayout?.filterModel
    ? Object.keys(savedLayout.filterModel)
      .filter(
        (x) =>
          x !== "advancedQuery" &&
            columnDefs
              .filter((x) => x?.filterParams?.external)
              .map((x) => x.field)
              .includes(x)
      )
      .reduce((obj, key) => {
        obj[key] = savedLayout.filterModel[key];
        return obj;
      }, {})
    : {};

  //initial state query with empty array of rules to not show any initial rule
  //change to null if you want to first render with one rule
  const [state, setState] = useState({
    query: savedLayout?.advancedQueryPanel?.query ?? { rules: [] },
    queryName: savedLayout?.advancedQueryPanel?.name ?? "",
    queryId: null,
    expanded: true,
    dialog: { open: false, action: ACTION_TYPES.clearAll },
  });

  useEffect(() => {
    //check if there's any selected applied/copied/edited from saved queries panel
    if (
      Object.keys(savedLayout?.advancedQueryPanel?.selected ?? {}).length > 0
    ) {
      setState((curState) => ({
        ...curState,
        query: savedLayout?.advancedQueryPanel?.selected.query,
      }));
      //case query applied from savedqueries panel
      //we want to apply filter directly to grid
      if (savedLayout?.advancedQueryPanel?.selected?.apply) {
        applyQueryHandler(
          savedLayout?.advancedQueryPanel?.selected.query,
          savedLayout?.advancedQueryPanel?.selected.name
        );
      } else {
        //in other case (copied, edited) we just want to display the query
        setState((curState) => ({
          ...curState,
          queryName: savedLayout?.advancedQueryPanel?.selected.name,
          expanded: true,
          queryId: savedLayout?.advancedQueryPanel?.selected.id,
        }));
      }
      //clear selected after apply
      dispatch(setSelectedQuery({ gridName, selected: null }));
    }
  }, [JSON.stringify(savedLayout?.advancedQueryPanel?.selected)]);

  const validateQuery = (query) => {
    let isValid = true;
    let checkRule = (rules) => {
      if (rules.length === 0) {
        isValid = false;
        return;
      } else {
        for (let index = 0; index < rules.length; index++) {
          const rule = rules[index];
          if (rule.rules) {
            checkRule(rule.rules);
          }
          if (
            typeof rule.value !== "undefined" &&
            (rule.value.trim().length === 0 || rule.value.trim() === "[]")
          ) {
            isValid = false;
          }

          const columnToCheck = columnDefs.find(
            (column) => column.field === rule.field
          );
          if (
            columnToCheck?.filterParams?.dataType === "date" &&
            rule.operator === "between"
          ) {
            isValid = validateDateRangePickerInput(rule.value);
          }
        }
      }
    };

    if (query.rules) {
      checkRule(query.rules);
    }
    return isValid;
  };

  const fields = useMemo(
    () =>
      columnDefs
        .filter(
          (colObj) =>
            typeof colObj.filterParams !== "undefined" &&
            Object.prototype.hasOwnProperty.call(
              colObj.filterParams,
              "advancedQuery"
            )
        )
        .map(
          (x) => ({
            name: x.field,
            label: x.filterParams?.advFilterLabel ?? x.headerName,
            dataType: x.filterParams?.dataType ?? "text",
            multiple:
            x.filterParams?.dataType !== "number" &&
            x.filterParams?.dataType !== "boolean",
            fetchData: () =>
              getGridFilterValues(
                filterValuesUrl,
                JSON.stringify({
                  colName: x.field,
                  filterModel: externalFilterModel,
                })
              ),
            filterParams: x.filterParams,
          })
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [JSON.stringify(externalFilterModel)]
  );

  const ruleProcessor = (r) => {
    return {
      ...r,
      multiple:
        (fields.find((f) => f.name === r.field).dataType === "text" &&
        (r.operator !== "contains" && r.operator !== "doesNotContain")) ||
        (fields.find((f) => f.name === r.field).dataType === "date" &&
          r.operator === "between"),
      dataType: fields.find((f) => f.name === r.field).dataType,
    };
  };

  const getQueryToApply = (query) => {
    if (query.rules.length > 0) {
      let result = transformQuery(query, { ruleProcessor });
      return JSON.stringify(result);
    }
    return null;
  };

  if (!savedLayout || !savedLayout?.advancedQueryPanel?.open) {
    return null;
  }

  const applyQueryHandler = (query, name = "") => {
    let areQueryRulesValid = validateQuery(query);
    if (!areQueryRulesValid) {
      dispatch(
        toastActions.showErrorMessage(
          t("components.datagrid.filters.advancedQuery.invalidErrorMessage")
        )
      );
      return false;
    }
    dispatch(setAdvancedQueryPanel({ open: true, query, gridName, name }));
    const queryToApply = getQueryToApply(query);
    onApplyFilters(queryToApply);
    setState((curState) => ({
      ...curState,
      expanded: false,
      queryName: name,
    }));
  };

  const clickActionHandler = (action) => {
    switch (action) {
    case ACTION_TYPES.clearAll:
      setState((curState) => ({
        ...curState,
        dialog: { open: true, action: ACTION_TYPES.clearAll },
      }));
      break;
    case ACTION_TYPES.cancelQuery:
      setState((curState) => ({
        ...curState,
        dialog: { open: true, action: ACTION_TYPES.cancelQuery },
      }));
      break;
    case ACTION_TYPES.saveQuery:
      let isQueryValid = validateQuery(state.query);
      if (!isQueryValid) {
        dispatch(
          toastActions.showErrorMessage(
            t("components.datagrid.filters.advancedQuery.invalidErrorMessage")
          )
        );
        break;
      }
      if (state.queryName.length === 0) {
        dispatch(
          toastActions.showErrorMessage(
            t(
              "components.datagrid.filters.advancedQuery.invalidNameErrorMessage"
            )
          )
        );
        break;
      }
      //case where editing an existing one
      if (state.queryId?.length > 0) {
        updateQuery({
          id: state.queryId,
          name: state.queryName,
          gridName,
          query: JSON.stringify(state.query),
        }).then((res) => {
          dispatch(
            toastActions.showSuccessMessage(
              t(
                "components.datagrid.filters.advancedQuery.updateQuerySuccessMessage"
              )
            )
          );
        });
      } else {
        saveQuery({
          name: state.queryName,
          gridName,
          query: JSON.stringify(state.query),
        }).then((res) => {
          dispatch(
            toastActions.showSuccessMessage(
              t(
                "components.datagrid.filters.advancedQuery.saveQuerySuccessMessage"
              )
            )
          );
        });
      }

      break;
    case ACTION_TYPES.applyFilters:
      applyQueryHandler(state.query, state.queryName);
      break;
    default:
      break;
    }
  };

  const closeDialogHandler = () => {
    setState((curState) => ({
      ...curState,
      dialog: { ...curState, open: false },
    }));
  };

  const confirmDialogHandler = () => {
    //cancel query action
    if (state.dialog.action === ACTION_TYPES.cancelQuery) {
      setState((curState) => ({
        ...curState,
        query: { rules: [] },
        queryName: "",
        queryId: null,
      }));
      dispatch(setAdvancedQueryPanel({ open: false, query: null, gridName }));
      onApplyFilters(null, true);
      //clear query action
    } else {
      setState((curState) => ({
        ...curState,
        query: { rules: [] },
        queryName: "",
        queryId: null,
      }));
    }
    closeDialogHandler();
  };

  return (
    <>
      <ConfirmationDialog
        message={
          state.dialog.action === ACTION_TYPES.clearAll
            ? t(
              "components.datagrid.filters.advancedQuery.confirmationDialog.clearAllMessage"
            )
            : t(
              "components.datagrid.filters.advancedQuery.confirmationDialog.cancelQueryMessage"
            )
        }
        open={state.dialog.open}
        onClose={closeDialogHandler}
        onConfirm={confirmDialogHandler}
      />
      <Wrapper>
        <SFAccordionCard
        // defaultExpanded
          expanded={state.expanded}
          onChange={() =>
            setState((curState) => ({
              ...curState,
              expanded: !curState.expanded,
            }))
          }
          title={t("components.datagrid.filters.advancedQuery.header")}
          titleVariant="cardSectionHeading"
        >
          <SFGridContainer spacing={1}>
            {Object.keys(savedLayout?.filterModel).filter(
              (x) =>
                x !== "advancedQuery" &&
                !columnDefs
                  .filter((x) => x?.filterParams?.external)
                  .map((x) => x.field)
                  .includes(x)
            ).length > 0 && (
              <SFGridItem xs={12}>
                <SFStackStyled spacing={2} direction="row" alignItems="center">
                  <SFInformationIcon size="xlarge" />
                  <SFTypography variant="h1" color="blue700">
                    {t("components.datagrid.filters.advancedQuery.infoLabel")}
                  </SFTypography>
                </SFStackStyled>
              </SFGridItem>
            )}
            <SFGridItem xs={12}>
              <SFTypography variant="body1">
                {t(
                  "components.datagrid.filters.advancedQuery.descriptionLabel"
                )}
              </SFTypography>
            </SFGridItem>
            <SFGridItem xs={12} md={6}>
              <SFTextField
                hiddenLabel
                variant="filled"
                value={state.queryName}
                onChange={(e) =>
                  setState((curState) => ({
                    ...curState,
                    queryName: e.target.value,
                  }))
                }
              />
            </SFGridItem>
          </SFGridContainer>
          <SFDividerStyled />
          <SFQueryBuilder
            fields={fields}
            query={state.query}
            advancedFilterProps = {advancedFilterProps}
            onChange={(q) =>
              setState((curState) => ({
                ...curState,
                query: q,
              }))
            }
          />
          <SFDividerActionsStyled />
          <Actions onClickAction={clickActionHandler} />
        </SFAccordionCard>
      </Wrapper>
    </>
  );
};

export default AdvancedQuery;
